import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useWarehouseContext } from "./WarehouseProvider";
import { ReactComponent as CameraSvg } from "./svgs/camera.svg";

import { PhotoConfirmation } from "./components/PhotoConfirmation";
import { StepHeader } from "./components/StepHeader";
import { FullscreenModal } from "./components/FullscreenModal";
import { Webcam } from "./components/Webcam";
import { useSteps } from "./useSteps";
import { RejectedBanner } from "./components/RejectedBanner";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./components/WarehouseButtonsFooter";

const TITLE = "Package Photo";

export const CapturePackagePhotos = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState("");
  const { packagePictures, addPackagePicture, currentTakeback } =
    useWarehouseContext();
  const { next } = useSteps();
  const webcamRef = useRef(null);
  const takePhotoButtonRef = useRef(null);

  const handleCapturePackage = useCallback(
    (picture) => {
      addPackagePicture(picture);
    },
    [addPackagePicture]
  );

  const capturePhoto = useCallback(() => {
    const photo = webcamRef.current?.getScreenshot();
    if (photo) {
      setCapturedPhoto(photo);
      setShowConfirmation(true);
    }
  }, []);

  const handleConfirmPhoto = useCallback(() => {
    const isFirstPhoto = packagePictures.length === 0;
    handleCapturePackage(capturedPhoto);
    setShowConfirmation(false);
    setCapturedPhoto("");
    // Auto-advance to next step after first photo
    if (isFirstPhoto) {
      next();
    }
  }, [capturedPhoto, handleCapturePackage, packagePictures.length, next]);

  const handleCancelPhoto = useCallback(() => {
    setShowConfirmation(false);
    setCapturedPhoto("");
  }, []);

  // Handle Enter key navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !isLoading) {
        e.preventDefault();

        // If showing confirmation, confirm the photo
        if (showConfirmation) {
          handleConfirmPhoto();
        }
        // If there are no pictures, capture a photo
        else if (packagePictures.length === 0) {
          capturePhoto();
        }
        // If there are pictures, capture another photo
        else {
          capturePhoto();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    packagePictures.length,
    next,
    isLoading,
    showConfirmation,
    handleConfirmPhoto,
    capturePhoto,
  ]);

  // Get reject category from takeback data
  const takebackData =
    typeof currentTakeback?.data === "object"
      ? currentTakeback?.data
      : JSON.parse(currentTakeback?.data || "{}");
  const rejectCategory = takebackData?.rejectCategory;

  return (
    <FullscreenModal>
      <div className="flex flex-col h-full">
        <Webcam ref={webcamRef} setIsLoading={setIsLoading} />
        <StepHeader opacity="100%" isCameraActive />
        {rejectCategory && (
          <div className="relative z-50">
            <RejectedBanner rejectCategory={rejectCategory} />
          </div>
        )}

        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="flex flex-col items-center gap-6 max-w-[672px]">
            <div className="flex flex-col items-center gap-4">
              <h1 className="text-[29px] font-bold text-white leading-[37px] tracking-[-0.01em]">
                {TITLE}
              </h1>
              <p className="text-[18px] font-normal text-[#C6CCE8] leading-[23px] text-center max-w-[600px]">
                Take a photo of the package with its label visible. Take
                additional photos of any package damages.
              </p>
            </div>
          </div>
        </div>

        <WarehouseButtonsFooter isCameraActive>
          <WarehouseButtonGroup columns={packagePictures.length > 0 ? 2 : 1}>
            <WarehouseButton
              ref={takePhotoButtonRef}
              variant="primary"
              onClick={capturePhoto}
              disabled={isLoading}
            >
              <div className="flex items-center justify-center gap-2">
                <CameraSvg className="fill-white w-5 h-5" />
                <span>Take Photo of Package with Label</span>
              </div>
            </WarehouseButton>
            {packagePictures.length > 0 && (
              <WarehouseButton variant="primary" onClick={next}>
                Continue
              </WarehouseButton>
            )}
          </WarehouseButtonGroup>
        </WarehouseButtonsFooter>
      </div>
      {showConfirmation &&
        createPortal(
          <PhotoConfirmation
            title={TITLE}
            photo={capturedPhoto}
            onConfirm={handleConfirmPhoto}
            onCancel={handleCancelPhoto}
            retakeButtonText="Retake Photo of Package with Label"
          />,
          document.body
        )}
    </FullscreenModal>
  );
};

CapturePackagePhotos.title = TITLE;
